import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  CheckCircle2,
  Download,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../../AuthContext";
import { markdownStyles } from "../Spend Chat Agent/sub_comp/styles";
import { apiRequest } from "./platformApi";
import { SupplierResultsTable } from "./SupplierResultsTable";
import { humanize, isMissing } from "./supplierFormat";

const API_BASE = "https://kearneysofacapim-prod-eastus.azure-api.net/agentkaibe";
const STORAGE_CLIENT_ID = "supplier_intelligence:client_id";
const STORAGE_SESSION_ID = "supplier_intelligence:session_id";

const safeSessionGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    if (value === undefined || value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, String(value));
    }
  } catch {
    /* ignore */
  }
};

const SupplierIntelligenceHome = () => {
  const { email } = useAuth();

  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);

  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [supplierLists, setSupplierLists] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState(() =>
    safeSessionGet(STORAGE_CLIENT_ID)
  );
  const [selectedSessionId, setSelectedSessionId] = useState(() =>
    safeSessionGet(STORAGE_SESSION_ID)
  );

  const [activeSession, setActiveSession] = useState(null);
  const [turns, setTurns] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);

  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [uiError, setUiError] = useState(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    subcategory: "",
    region: "europe",
  });

  const [selectedSupplierRows, setSelectedSupplierRows] = useState(new Set());
  const [expandedSupplierRows, setExpandedSupplierRows] = useState(new Set());

  const [successToast, setSuccessToast] = useState(null);
  const [deleteChatBusy, setDeleteChatBusy] = useState(false);

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [createClientBusy, setCreateClientBusy] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom, turns, sending]);

  useEffect(() => {
    if (!successToast) return;
    const t = setTimeout(() => setSuccessToast(null), 2200);
    return () => clearTimeout(t);
  }, [successToast]);

  const loadBootstrap = useCallback(async () => {
    if (!email) return;
    setBootstrapping(true);
    setBootstrapError(null);
    setUiError(null);

    try {
      const data = await apiRequest(API_BASE, {
        path: "/supplier-discovery/platform/bootstrap",
        query: { email_id: email, include_turns: false },
      });

      const nextClients = Array.isArray(data?.clients) ? data.clients : [];
      setClients(nextClients);

      const storedClientId = safeSessionGet(STORAGE_CLIENT_ID);
      const fallbackClientId = nextClients[0]?.client_id || null;
      const resolvedClientId =
        storedClientId &&
        nextClients.some((c) => c.client_id === storedClientId)
          ? storedClientId
          : fallbackClientId;

      setSelectedClientId(resolvedClientId);
    } catch (err) {
      setBootstrapError(err?.message || "Failed to bootstrap Supplier Intelligence.");
    } finally {
      setBootstrapping(false);
    }
  }, [email]);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  const refreshClientContext = useCallback(async () => {
    if (!email || !selectedClientId) return;

    safeSessionSet(STORAGE_CLIENT_ID, selectedClientId);

    try {
      const [chatsData, listsData] = await Promise.all([
        apiRequest(API_BASE, {
          path: "/supplier-discovery/platform/chats",
          query: { email_id: email, client_id: selectedClientId },
        }),
        apiRequest(API_BASE, {
          path: "/supplier-discovery/platform/supplier-lists",
          query: { email_id: email, client_id: selectedClientId },
        }),
      ]);

      const nextSessions = Array.isArray(chatsData?.sessions)
        ? chatsData.sessions
        : [];
      setSessions(nextSessions);

      const nextLists = Array.isArray(listsData?.files) ? listsData.files : [];
      setSupplierLists(nextLists);

      const storedSessionId = safeSessionGet(STORAGE_SESSION_ID);
      const fallbackSessionId = nextSessions[0]?.session_id || null;
      const resolvedSessionId =
        selectedSessionId && nextSessions.some((s) => s.session_id === selectedSessionId)
          ? selectedSessionId
          : storedSessionId && nextSessions.some((s) => s.session_id === storedSessionId)
            ? storedSessionId
            : fallbackSessionId;

      if (resolvedSessionId !== selectedSessionId) {
        setTurns([]);
        setActiveSession(null);
        setSelectedSupplierRows(new Set());
        setExpandedSupplierRows(new Set());
      }

      setSelectedSessionId(resolvedSessionId);
    } catch (err) {
      setUiError(err?.message || "Failed to refresh client workspace.");
    }
  }, [email, selectedClientId, selectedSessionId]);

  useEffect(() => {
    refreshClientContext();
  }, [refreshClientContext]);

  const loadSession = useCallback(async () => {
    if (!email || !selectedClientId || !selectedSessionId) return;

    safeSessionSet(STORAGE_SESSION_ID, selectedSessionId);

    setLoadingSession(true);
    setUiError(null);

    try {
      const data = await apiRequest(API_BASE, {
        path: `/supplier-discovery/platform/chats/${encodeURIComponent(
          selectedSessionId
        )}`,
        query: { email_id: email, client_id: selectedClientId, include_turns: true },
      });

      const nextTurns = Array.isArray(data?.turns) ? data.turns : [];
      setActiveSession(data?.session || null);
      setTurns((prev) => {
        const prior = Array.isArray(prev) ? prev : [];
        const pending = prior.filter((t) => t && t.pending);
        if (pending.length === 0) return nextTurns;

        const existingIds = new Set(
          nextTurns.map((t) => String(t?.turn_id))
        );
        const pendingToKeep = pending.filter(
          (t) => !existingIds.has(String(t?.turn_id))
        );
        return [...nextTurns, ...pendingToKeep];
      });
      setSelectedSupplierRows(new Set());
      setExpandedSupplierRows(new Set());
    } catch (err) {
      setUiError(err?.message || "Failed to load chat history.");
    } finally {
      setLoadingSession(false);
    }
  }, [email, selectedClientId, selectedSessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.client_id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const createChat = useCallback(async () => {
    if (!email || !selectedClientId) return null;

    const data = await apiRequest(API_BASE, {
      path: "/supplier-discovery/platform/chats",
      method: "POST",
      body: {
        email_id: email,
        client_id: selectedClientId,
        title: null,
      },
    });

    const created = {
      session_id: data?.session_id,
      email_id: data?.email_id,
      client_id: data?.client_id,
      title: data?.title || null,
      created_at: data?.created_at,
      updated_at: data?.updated_at,
      turn_count: 0,
    };

    if (!created.session_id) return null;
    setSessions((prev) => [created, ...(prev || [])]);
    setSelectedSessionId(created.session_id);
    setActiveSession(created);
    setTurns([]);
    return created.session_id;
  }, [email, selectedClientId]);

  const handleNewChat = useCallback(async () => {
    if (!selectedClientId) return;
    setUiError(null);
    try {
      await createChat();
    } catch (err) {
      setUiError(err?.message || "Failed to create chat.");
    }
  }, [createChat, selectedClientId]);

  const handleDeleteChat = useCallback(async () => {
    if (!email || !selectedClientId || !selectedSessionId) return;
    if (deleteChatBusy) return;

    const sessionId = selectedSessionId;

    const ok = window.confirm(
      "Delete this chat? This removes the stored chat history and supplier-list index entries for this session. CSV blobs are not deleted."
    );
    if (!ok) return;

    setDeleteChatBusy(true);
    setUiError(null);

    try {
      await apiRequest(API_BASE, {
        path: `/supplier-discovery/platform/chats/${encodeURIComponent(sessionId)}`,
        method: "DELETE",
        query: {
          email_id: email,
          client_id: selectedClientId,
        },
      });

      setSuccessToast("Chat deleted");
      safeSessionSet(STORAGE_SESSION_ID, null);
      setSelectedSessionId(null);
      setActiveSession(null);
      setTurns([]);
      setSelectedSupplierRows(new Set());
      setExpandedSupplierRows(new Set());

      await refreshClientContext();
    } catch (err) {
      setUiError(err?.message || "Failed to delete chat.");
    } finally {
      setDeleteChatBusy(false);
    }
  }, [
    deleteChatBusy,
    email,
    refreshClientContext,
    selectedClientId,
    selectedSessionId,
  ]);

  const handleCreateClient = useCallback(async () => {
    if (!email) return;
    const clientName = newClientName.trim();
    if (!clientName) return;
    if (createClientBusy) return;

    setCreateClientBusy(true);
    setUiError(null);

    try {
      const data = await apiRequest(API_BASE, {
        path: "/supplier-discovery/platform/clients",
        method: "POST",
        body: { email_id: email, client_name: clientName },
      });

      const created = data?.client;
      if (!created?.client_id) {
        throw new Error("Client create succeeded but returned no client_id.");
      }

      setClients((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const without = existing.filter((c) => c.client_id !== created.client_id);
        return [created, ...without];
      });

      setShowCreateClient(false);
      setNewClientName("");
      setSelectedClientId(created.client_id);
      setSuccessToast("Client created");
    } catch (err) {
      setUiError(err?.message || "Failed to create client.");
    } finally {
      setCreateClientBusy(false);
    }
  }, [createClientBusy, email, newClientName]);

  const handleDeleteClient = useCallback(
    async (clientId) => {
      if (!email || !clientId) return;
      if (
        !window.confirm(
          `Delete client "${clientId}" and all its chats / lists?`
        )
      ) {
        return;
      }

      setUiError(null);

      try {
        await apiRequest(API_BASE, {
          path: `/supplier-discovery/platform/clients/${encodeURIComponent(
            clientId
          )}`,
          method: "DELETE",
          query: { email_id: email },
        });

        setClients((prev) =>
          (Array.isArray(prev) ? prev : []).filter((c) => c.client_id !== clientId)
        );

        if (clientId === selectedClientId) {
          setSelectedClientId(null);
          setSelectedSessionId(null);
          setSessions([]);
          setSupplierLists([]);
          setTurns([]);
          setActiveSession(null);
          safeSessionSet(STORAGE_CLIENT_ID, null);
          safeSessionSet(STORAGE_SESSION_ID, null);
        }

        setSuccessToast("Client deleted");
      } catch (err) {
        setUiError(err?.message || "Failed to delete client.");
      }
    },
    [email, selectedClientId]
  );

  const handleToggleSelectedSupplier = useCallback((rowId) => {
    setSelectedSupplierRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleToggleAllSelectedSupplier = useCallback((rowIds, checked) => {
    setSelectedSupplierRows((prev) => {
      const next = new Set(prev);
      rowIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const handleToggleExpandedSupplier = useCallback((rowId) => {
    setExpandedSupplierRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleDownloadSelected = useCallback(() => {
    if (!selectedSupplierRows.size) return;

    const selectedRows = [];
    turns.forEach((turn) => {
      const supplierRows = Array.isArray(turn?.supplier_rows)
        ? turn.supplier_rows
        : [];
      supplierRows.forEach((row, idx) => {
        const key = `${turn.turn_id}-${idx}`;
        if (selectedSupplierRows.has(key)) {
          selectedRows.push(row);
        }
      });
    });

    if (!selectedRows.length) return;
    const allKeys = Array.from(
      new Set(selectedRows.flatMap((r) => Object.keys(r || {})))
    );

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [
      allKeys.join(","),
      ...selectedRows.map((row) => allKeys.map((k) => escape(row?.[k])).join(",")),
    ];

    try {
      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "supplier_shortlist.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, [selectedSupplierRows, turns]);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!email || !selectedClientId) return;
    if (sending) return;

    setUiError(null);
    setSending(true);

    const sessionId = selectedSessionId || (await createChat());
    if (!sessionId) {
      setSending(false);
      setUiError("Unable to create a chat session.");
      return;
    }

    const pendingId = `pending_${Date.now()}`;
    setTurns((prev) => [
      ...(prev || []),
      {
        turn_id: pendingId,
        session_id: sessionId,
        user_message: trimmed,
        assistant_message: "",
        supplier_rows: [],
        pending: true,
        created_at: new Date().toISOString(),
      },
    ]);
    setInputValue("");

    const query = trimmed;

    try {
      const data = await apiRequest(API_BASE, {
        path: `/supplier-discovery/platform/chats/${encodeURIComponent(
          sessionId
        )}/message`,
        method: "POST",
        body: {
          email_id: email,
          client_id: selectedClientId,
          query,
          max_steps: 5,
        },
      });

      const supplierList = data?.supplier_list || null;

      const nextTurn = {
        turn_id: data?.turn_id ?? pendingId,
        session_id: sessionId,
        user_message: trimmed,
        assistant_message: data?.answer || "",
        supplier_rows: Array.isArray(data?.supplier_rows) ? data.supplier_rows : [],
        supplier_list_blob_path: supplierList?.blob_path,
        supplier_list_download_url: supplierList?.download_url,
        supplier_list_file_name: supplierList?.file_name,
        supplier_list_row_count: supplierList?.row_count,
        supplier_list_region: supplierList?.region,
        supplier_list_l1: supplierList?.l1,
        supplier_list_l2: supplierList?.l2,
        supplier_list_l3: supplierList?.l3,
        stored: data?.stored,
        store_error: data?.store_error,
        supplier_list_error: data?.supplier_list_error,
        created_at: data?.turn_created_at || new Date().toISOString(),
      };

      setTurns((prev) =>
        (prev || []).map((t) => (t.turn_id === pendingId ? nextTurn : t))
      );

      if (nextTurn.stored === false || nextTurn.supplier_list_error) {
        setSuccessToast("Answer returned (storage warning)");
      }

      Promise.allSettled([
        apiRequest(API_BASE, {
          path: "/supplier-discovery/platform/chats",
          query: { email_id: email, client_id: selectedClientId },
        }).then((chatsData) => {
          const nextSessions = Array.isArray(chatsData?.sessions)
            ? chatsData.sessions
            : [];
          setSessions(nextSessions);
        }),
        apiRequest(API_BASE, {
          path: "/supplier-discovery/platform/supplier-lists",
          query: { email_id: email, client_id: selectedClientId },
        }).then((listsData) => {
          const nextLists = Array.isArray(listsData?.files)
            ? listsData.files
            : [];
          setSupplierLists(nextLists);
        }),
      ]).catch(() => {});
    } catch (err) {
      setTurns((prev) =>
        (prev || []).map((t) =>
          t.turn_id === pendingId
            ? {
                ...t,
                pending: false,
                assistant_message:
                  "Sorry, I couldn't load results for that request. Please try again in a moment.",
                error: true,
              }
            : t
        )
      );
      setUiError(err?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  }, [
    createChat,
    email,
    inputValue,
    selectedClientId,
    selectedSessionId,
    sending,
  ]);

  const sortedSupplierLists = useMemo(() => {
    const items = Array.isArray(supplierLists) ? supplierLists : [];
    const sorted = [...items].sort((a, b) => {
      const at = a?.created_at ? Date.parse(a.created_at) : 0;
      const bt = b?.created_at ? Date.parse(b.created_at) : 0;
      return bt - at;
    });
    return sorted;
  }, [supplierLists]);

  const totalSelected = selectedSupplierRows.size || 0;
  const chatSelectOptions = useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    return sessions.map((s) => {
      const id = s?.session_id;
      const label =
        s?.title ||
        s?.last_user_message ||
        `Chat ${String(id || "").slice(0, 8)}`;
      return { id, label };
    });
  }, [sessions]);

  const sidebarItemStyle = (active) => ({
    width: "100%",
    textAlign: "left",
    border: active ? "1px solid rgba(99,102,241,0.55)" : "1px solid transparent",
    backgroundColor: active ? "rgba(99,102,241,0.15)" : "transparent",
    color: active ? "#e0e7ff" : "#e5e7eb",
    padding: "10px 10px",
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  });

  if (!email) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          backgroundColor: "#020617",
          color: "#e5e7eb",
        }}
      >
        Loading user…
      </div>
    );
  }

  if (bootstrapping) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          backgroundColor: "#020617",
          color: "#e5e7eb",
        }}
      >
        Loading Supplier Intelligence…
      </div>
    );
  }

  if (bootstrapError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          backgroundColor: "#020617",
          color: "#e5e7eb",
        }}
      >
        <div
          style={{
            maxWidth: 520,
            borderRadius: 16,
            border: "1px solid rgba(239,68,68,0.45)",
            backgroundColor: "rgba(69,10,10,0.65)",
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Bootstrap failed</div>
          <div style={{ color: "#fecaca", marginBottom: 12 }}>{bootstrapError}</div>
          <button
            type="button"
            onClick={loadBootstrap}
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.25)",
              backgroundColor: "rgba(2,6,23,0.8)",
              color: "#e5e7eb",
              cursor: "pointer",
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 20% 0%, rgba(99,102,241,.12) 0%, rgba(2,6,23,0) 60%), radial-gradient(900px 500px at 90% 10%, rgba(236,72,153,.10) 0%, rgba(2,6,23,0) 65%), #020617",
        color: "#e5e7eb",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          width: 330,
          borderRight: "1px solid rgba(31,41,55,0.9)",
          backgroundColor: "rgba(2,6,23,0.85)",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              marginBottom: 6,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 16 }}>
              Supplier Intelligence
            </div>
            <button
              type="button"
              onClick={() => setShowCreateClient(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 10px",
                borderRadius: 12,
                border: "1px solid rgba(99,102,241,0.35)",
                backgroundColor: "rgba(99,102,241,0.15)",
                color: "#e0e7ff",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
              title="Create client"
            >
              <Plus size={14} />
              Client
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>{email}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Clients</span>
            {selectedClientId && (
              <button
                type="button"
                onClick={() => handleDeleteClient(selectedClientId)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#fca5a5",
                  cursor: "pointer",
                  padding: 6,
                  borderRadius: 10,
                }}
                title="Delete selected client"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {clients.length === 0 ? (
              <div
                style={{
                  borderRadius: 14,
                  border: "1px dashed rgba(148,163,184,0.25)",
                  padding: 12,
                  fontSize: 13,
                  color: "#94a3b8",
                }}
              >
                No clients yet. Create one to begin.
              </div>
            ) : (
              clients.map((client) => {
                const active = client.client_id === selectedClientId;
                return (
                  <button
                    key={client.client_id}
                    type="button"
                    style={sidebarItemStyle(active)}
                    onClick={() => setSelectedClientId(client.client_id)}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 12,
                        display: "grid",
                        placeItems: "center",
                        background:
                          "linear-gradient(135deg, rgba(99,102,241,0.35), rgba(236,72,153,0.25))",
                        border: "1px solid rgba(148,163,184,0.25)",
                        flexShrink: 0,
                      }}
                    >
                      <MessageSquare size={16} color="#e0e7ff" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {client.client_name || client.client_id}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#94a3b8",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {client.client_id}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Chats</span>
            <button
              type="button"
              disabled={!selectedClientId}
              onClick={handleNewChat}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.25)",
                backgroundColor: "rgba(2,6,23,0.65)",
                color: selectedClientId ? "#e5e7eb" : "#64748b",
                cursor: selectedClientId ? "pointer" : "not-allowed",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <Plus size={14} />
              New
            </button>
          </div>

          <div
            style={{
              overflowY: "auto",
              paddingRight: 4,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {!selectedClientId ? (
              <div
                style={{
                  borderRadius: 14,
                  border: "1px dashed rgba(148,163,184,0.25)",
                  padding: 12,
                  fontSize: 13,
                  color: "#94a3b8",
                }}
              >
                Select a client to see chats.
              </div>
            ) : sessions.length === 0 ? (
              <div
                style={{
                  borderRadius: 14,
                  border: "1px dashed rgba(148,163,184,0.25)",
                  padding: 12,
                  fontSize: 13,
                  color: "#94a3b8",
                }}
              >
                No chats yet. Create one to begin.
              </div>
            ) : (
              sessions.map((s) => {
                const active = s.session_id === selectedSessionId;
                const title =
                  s.title ||
                  s.last_user_message ||
                  `Chat ${String(s.session_id || "").slice(0, 8)}`;
                return (
                  <button
                    key={s.session_id}
                    type="button"
                    style={sidebarItemStyle(active)}
                    onClick={() => setSelectedSessionId(s.session_id)}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 12,
                        display: "grid",
                        placeItems: "center",
                        background: active
                          ? "rgba(236,72,153,0.20)"
                          : "rgba(148,163,184,0.10)",
                        border: "1px solid rgba(148,163,184,0.25)",
                        flexShrink: 0,
                      }}
                    >
                      <MessageSquare size={16} color={active ? "#fda4af" : "#cbd5e1"} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#94a3b8",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.turn_count || 0} turn{s.turn_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Top bar */}
        <div
          style={{
            padding: "20px 24px 14px",
            borderBottom: "1px solid rgba(31,41,55,0.9)",
            backgroundColor: "rgba(2,6,23,0.75)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  background:
                    "linear-gradient(90deg, #f97316, #ec4899, #6366f1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {selectedClient?.client_name || "Select a client"}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>
                {selectedClientId ? (
                  <>
                    Client <span style={{ color: "#e5e7eb" }}>{selectedClientId}</span>
                    {activeSession?.session_id && (
                      <>
                        {" "}
                        · Session{" "}
                        <span style={{ color: "#e5e7eb" }}>
                          {String(activeSession.session_id).slice(0, 12)}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  "Create or select a client to begin."
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.25)",
                  backgroundColor: "rgba(2,6,23,0.65)",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#64748b",
                  }}
                >
                  Chat
                </span>
                <select
                  value={selectedSessionId || ""}
                  onChange={(e) => setSelectedSessionId(e.target.value || null)}
                  disabled={!selectedClientId || chatSelectOptions.length === 0}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color:
                      selectedClientId && chatSelectOptions.length
                        ? "#e5e7eb"
                        : "#64748b",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor:
                      selectedClientId && chatSelectOptions.length
                        ? "pointer"
                        : "not-allowed",
                    maxWidth: 220,
                  }}
                >
                  {chatSelectOptions.length === 0 ? (
                    <option value="">No chats</option>
                  ) : (
                    chatSelectOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <button
                type="button"
                disabled={!selectedSessionId || deleteChatBusy}
                onClick={handleDeleteChat}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(239,68,68,0.35)",
                  backgroundColor: "rgba(239,68,68,0.14)",
                  color: "#fecaca",
                  cursor:
                    !selectedSessionId || deleteChatBusy ? "not-allowed" : "pointer",
                  opacity: !selectedSessionId || deleteChatBusy ? 0.6 : 1,
                  fontSize: 12,
                  fontWeight: 800,
                }}
                title="Delete this chat session"
              >
                <Trash2 size={16} />
                {deleteChatBusy ? "Deleting…" : "Delete chat"}
              </button>

              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.25)",
                  backgroundColor: "rgba(2,6,23,0.65)",
                  color: "#e5e7eb",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <Settings size={16} />
                Filters
              </button>

              <button
                type="button"
                disabled={!totalSelected}
                onClick={handleDownloadSelected}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(16,185,129,0.35)",
                  backgroundColor: "rgba(16,185,129,0.12)",
                  color: "#bbf7d0",
                  cursor: totalSelected ? "pointer" : "not-allowed",
                  opacity: totalSelected ? 1 : 0.5,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                <Download size={16} />
                Download selected ({totalSelected})
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              style={{
                marginTop: 14,
                borderRadius: 16,
                border: "1px solid rgba(31,41,55,0.9)",
                backgroundColor: "rgba(2,6,23,0.6)",
                padding: 12,
                maxWidth: 920,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#64748b",
                      marginBottom: 6,
                    }}
                  >
                    Category
                  </label>
                  <input
                    value={filters.category}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, category: e.target.value }))
                    }
                    placeholder="e.g., Corporate travel"
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.25)",
                      backgroundColor: "rgba(2,6,23,0.6)",
                      color: "#e5e7eb",
                      padding: "10px 12px",
                      outline: "none",
                      fontSize: 13,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#64748b",
                      marginBottom: 6,
                    }}
                  >
                    Subcategory
                  </label>
                  <input
                    value={filters.subcategory}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, subcategory: e.target.value }))
                    }
                    placeholder="e.g., Managed travel"
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.25)",
                      backgroundColor: "rgba(2,6,23,0.6)",
                      color: "#e5e7eb",
                      padding: "10px 12px",
                      outline: "none",
                      fontSize: 13,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#64748b",
                      marginBottom: 6,
                    }}
                  >
                    Region
                  </label>
                  <select
                    value={filters.region}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, region: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.25)",
                      backgroundColor: "rgba(2,6,23,0.6)",
                      color: "#e5e7eb",
                      padding: "10px 12px",
                      outline: "none",
                      fontSize: 13,
                    }}
                  >
                    <option value="europe">Europe</option>
                    <option value="americas">Americas</option>
                    <option value="asia_pacific">Asia Pacific</option>
                    <option value="middle_east_africa">Middle East &amp; Africa</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Supplier lists ribbon */}
          {selectedClientId && (
            <div
              style={{
                marginTop: 14,
                borderRadius: 16,
                border: "1px solid rgba(31,41,55,0.9)",
                backgroundColor: "rgba(2,6,23,0.6)",
                padding: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                  Supplier lists collected so far
                </div>
                <button
                  type="button"
                  onClick={refreshClientContext}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid rgba(148,163,184,0.25)",
                    backgroundColor: "rgba(2,6,23,0.65)",
                    color: "#e5e7eb",
                    padding: "8px 10px",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>

              {sortedSupplierLists.length === 0 ? (
                <div
                  style={{
                    fontSize: 13,
                    color: "#64748b",
                    border: "1px dashed rgba(148,163,184,0.25)",
                    padding: 12,
                    borderRadius: 14,
                  }}
                >
                  No supplier lists yet. Ask a question to generate one.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    maxHeight: 160,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {sortedSupplierLists.map((f) => {
                    const rowCount =
                      typeof f?.row_count === "number" ? f.row_count : null;
                    const countLabel =
                      rowCount === null ? "Some" : rowCount.toLocaleString();
                    const plural = rowCount === 1 ? "" : "s";

                    const normalizeFacet = (val) => {
                      if (isMissing(val)) return null;
                      const s = String(val).trim();
                      if (!s) return null;
                      const lower = s.toLowerCase();
                      if (lower === "all") return "All";
                      if (lower === "mixed") return "Mixed";
                      return humanize(s);
                    };

                    const regionRaw = normalizeFacet(f?.region);
                    const regionLabel =
                      regionRaw === "All" || !regionRaw
                        ? "All regions"
                        : regionRaw === "Mixed"
                          ? "Mixed regions"
                          : regionRaw;

                    const l1 = normalizeFacet(f?.l1);
                    const l2 = normalizeFacet(f?.l2);
                    const l3 = normalizeFacet(f?.l3);

                    const categorySegments = [l1, l2, l3].filter(
                      (seg) => seg && seg !== "All"
                    );

                    const categoryLabel =
                      categorySegments.length > 0
                        ? categorySegments.join(" > ")
                        : l1 === "Mixed" || l2 === "Mixed" || l3 === "Mixed"
                          ? "Mixed categories"
                          : "All categories";

                    const primaryLabel = `${countLabel} supplier${plural} servicing ${categoryLabel} in ${regionLabel}`;
                    const fileName = f?.file_name || "supplier_list.csv";
                    const metaLine = fileName;

                    return (
                      <a
                        key={f?.blob_path || `${fileName}-${f?.turn_id || ""}`}
                        href={f?.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          borderRadius: 14,
                          border: "1px solid rgba(148,163,184,0.22)",
                          backgroundColor: "rgba(2,6,23,0.55)",
                          color: "#e5e7eb",
                          textDecoration: "none",
                        }}
                        title={primaryLabel}
                      >
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 12,
                            display: "grid",
                            placeItems: "center",
                            backgroundColor: "rgba(2,6,23,0.55)",
                            border: "1px solid rgba(148,163,184,0.25)",
                            flexShrink: 0,
                          }}
                        >
                          <Download size={16} color="#c7d2fe" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {primaryLabel}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#94a3b8",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {metaLine}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "22px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {!selectedClientId ? (
            <div
              style={{
                flex: 1,
                display: "grid",
                placeItems: "center",
                color: "#94a3b8",
              }}
            >
              Select a client on the left to begin.
            </div>
          ) : !selectedSessionId ? (
            <div
              style={{
                flex: 1,
                display: "grid",
                placeItems: "center",
                color: "#94a3b8",
              }}
            >
              Select a chat or create a new one to begin.
            </div>
          ) : loadingSession ? (
            <div
              style={{
                flex: 1,
                display: "grid",
                placeItems: "center",
                color: "#94a3b8",
              }}
            >
              Loading chat…
            </div>
          ) : turns.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#e5e7eb" }}>
                  Supplier Intelligence Copilot
                </div>
                <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 14 }}>
                  Ask for suppliers, refine filters, or request more detail.
                </div>
              </div>
            </div>
          ) : (
            turns.map((turn) => {
              const pending = Boolean(turn?.pending);
              const userText = turn?.user_message || "";
              const assistantText = turn?.assistant_message || "";
              const supplierRows = Array.isArray(turn?.supplier_rows)
                ? turn.supplier_rows
                : [];

              const turnKey = turn?.turn_id ?? `turn_${Math.random()}`;
              const listDownloadUrl =
                turn?.supplier_list_download_url || turn?.supplier_list?.download_url;
              const listFileName =
                turn?.supplier_list_file_name || turn?.supplier_list?.file_name;

              return (
                <div key={turnKey} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* User bubble */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div
                      style={{
                        maxWidth: 860,
                        borderRadius: 16,
                        padding: "14px 16px",
                        background: "linear-gradient(135deg, #6366f1, #ec4899)",
                        border: "1px solid rgba(191,219,254,0.35)",
                        color: "#f8fafc",
                        boxShadow: "0 16px 30px rgba(30,64,175,0.35)",
                        fontSize: 14,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {userText}
                    </div>
                  </div>

                  {/* Assistant bubble */}
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <div
                      style={{
                        maxWidth: 860,
                        borderRadius: 16,
                        padding: "14px 16px",
                        backgroundColor: "rgba(2,6,23,0.65)",
                        border: "1px solid rgba(31,41,55,0.9)",
                        color: "#e5e7eb",
                        boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                        fontSize: 14,
                      }}
                    >
                      {pending ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              backgroundColor: "#94a3b8",
                              animation: "pulse 1s infinite",
                            }}
                          />
                          <div style={{ color: "#94a3b8", fontSize: 13 }}>
                            Thinking…
                          </div>
                        </div>
                      ) : (
                        <div style={markdownStyles.container}>
                          <ReactMarkdown
                            components={{
                              h1: ({ node, children, ...props }) => (
                                <h1 style={markdownStyles.h1} {...props}>
                                  {children}
                                </h1>
                              ),
                              h2: ({ node, children, ...props }) => (
                                <h2 style={markdownStyles.h2} {...props}>
                                  {children}
                                </h2>
                              ),
                              p: ({ node, ...props }) => (
                                <p style={markdownStyles.p} {...props} />
                              ),
                              code: ({ node, inline, ...props }) => (
                                <code style={markdownStyles.code} {...props} />
                              ),
                              pre: ({ node, ...props }) => (
                                <pre style={markdownStyles.pre} {...props} />
                              ),
                              ul: ({ node, ...props }) => (
                                <ul style={markdownStyles.ul} {...props} />
                              ),
                              li: ({ node, ...props }) => (
                                <li style={markdownStyles.li} {...props} />
                              ),
                              strong: ({ node, ...props }) => (
                                <strong style={markdownStyles.strong} {...props} />
                              ),
                              a: ({ href, children, ...props }) => (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={markdownStyles.a}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.open(href, "_blank", "noopener,noreferrer");
                                  }}
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {assistantText}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Supplier results */}
                  {supplierRows.length > 0 && (
                    <SupplierResultsTable
                      rows={supplierRows}
                      turnKey={String(turnKey)}
                      selectedRowIds={selectedSupplierRows}
                      expandedRowIds={expandedSupplierRows}
                      onToggleRowSelected={handleToggleSelectedSupplier}
                      onToggleAllSelected={handleToggleAllSelectedSupplier}
                      onToggleExpanded={handleToggleExpandedSupplier}
                      downloadUrl={listDownloadUrl}
                      downloadLabel={
                        !isMissing(listFileName) ? listFileName : "Supplier list"
                      }
                    />
                  )}
                </div>
              );
            })
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          style={{
            borderTop: "1px solid rgba(31,41,55,0.9)",
            padding: "14px 24px",
            backgroundColor: "rgba(2,6,23,0.75)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div
              style={{
                flex: 1,
                borderRadius: 16,
                border: "1px solid rgba(148,163,184,0.25)",
                backgroundColor: "rgba(2,6,23,0.6)",
                padding: 10,
              }}
            >
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  selectedClientId
                    ? "Ask for suppliers, refine filters, or request more details…"
                    : "Select a client to start…"
                }
                rows={2}
                disabled={!selectedClientId || sending}
                style={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  background: "transparent",
                  color: "#e5e7eb",
                  fontSize: 14,
                  fontFamily: "inherit",
                }}
              />
              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                Enter to send · Shift+Enter for newline
              </div>
            </div>

            <button
              type="button"
              onClick={handleSend}
              disabled={!selectedClientId || !inputValue.trim() || sending}
              style={{
                borderRadius: 16,
                border: "none",
                padding: 12,
                cursor:
                  !selectedClientId || !inputValue.trim() || sending
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !selectedClientId || !inputValue.trim() || sending ? 0.6 : 1,
                background:
                  "linear-gradient(135deg, #f97316, #ec4899, #6366f1)",
                color: "#020617",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 52,
              }}
              title="Send"
            >
              <Send size={18} />
            </button>
          </div>

          {uiError && (
            <div
              style={{
                marginTop: 10,
                borderRadius: 12,
                padding: "10px 12px",
                backgroundColor: "rgba(69,10,10,0.65)",
                border: "1px solid rgba(239,68,68,0.45)",
                color: "#fecaca",
                fontSize: 13,
              }}
            >
              {uiError}
            </div>
          )}
        </div>
      </main>

      {/* Toast */}
      {successToast && (
        <div
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 5000,
            backgroundColor: "rgba(2,6,23,0.85)",
            border: "1px solid rgba(34,197,94,0.35)",
            borderRadius: 14,
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: "#bbf7d0",
            boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
          }}
        >
          <CheckCircle2 size={18} color="#22c55e" />
          <div style={{ fontSize: 13, fontWeight: 700 }}>{successToast}</div>
        </div>
      )}

      {/* Create client modal */}
      {showCreateClient && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => {
            if (createClientBusy) return;
            setShowCreateClient(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 6000,
            backgroundColor: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(6px)",
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 520,
              borderRadius: 18,
              border: "1px solid rgba(31,41,55,0.9)",
              backgroundColor: "rgba(2,6,23,0.95)",
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 16 }}>
                Create client
              </div>
              <button
                type="button"
                disabled={createClientBusy}
                onClick={() => setShowCreateClient(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: createClientBusy ? "not-allowed" : "pointer",
                  padding: 6,
                  borderRadius: 12,
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12 }}>
              Enter a display name; the backend will derive a stable client ID.
            </div>

            <input
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              placeholder="e.g., ACME Inc."
              style={{
                width: "100%",
                borderRadius: 14,
                border: "1px solid rgba(148,163,184,0.25)",
                backgroundColor: "rgba(2,6,23,0.6)",
                color: "#e5e7eb",
                padding: "12px 12px",
                outline: "none",
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                type="button"
                disabled={createClientBusy}
                onClick={() => setShowCreateClient(false)}
                style={{
                  flex: 1,
                  padding: "12px 12px",
                  borderRadius: 14,
                  border: "1px solid rgba(148,163,184,0.25)",
                  backgroundColor: "rgba(2,6,23,0.6)",
                  color: "#e5e7eb",
                  cursor: createClientBusy ? "not-allowed" : "pointer",
                  fontWeight: 800,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateClient}
                disabled={createClientBusy || !newClientName.trim()}
                style={{
                  flex: 1,
                  padding: "12px 12px",
                  borderRadius: 14,
                  border: "none",
                  background:
                    "linear-gradient(135deg, #f97316, #ec4899, #6366f1)",
                  color: "#020617",
                  cursor:
                    createClientBusy || !newClientName.trim()
                      ? "not-allowed"
                      : "pointer",
                  opacity: createClientBusy || !newClientName.trim() ? 0.6 : 1,
                  fontWeight: 900,
                }}
              >
                {createClientBusy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.55; }
            50% { transform: scale(1.25); opacity: 1; }
            100% { transform: scale(1); opacity: 0.55; }
          }
        `}
      </style>
    </div>
  );
};

export default SupplierIntelligenceHome;
